import { Page, Text, View } from '@react-pdf/renderer'
import { Button, Form, Input, message } from 'antd'
import GeckoLogo from 'features/reporting/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { ChecklistFindingsData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useCallback, useMemo } from 'react'
import {
  GeckoVisualInspectionFieldResponse,
  GeckoVisualInspectionSectionResponse,
} from 'types'

const requiredChecklistFindingsFields = ['scope']

interface ChecklistFinding {
  category: string
  name: string
  value: string
  findingSummary: string
}

// Shared function to process checklist findings from section and field responses
const processChecklistFindings = (
  sectionResponses: GeckoVisualInspectionSectionResponse[],
  fieldResponses: GeckoVisualInspectionFieldResponse[],
  uppercase = false
): ChecklistFinding[] => {
  const findings: ChecklistFinding[] = []

  // Group field responses by section
  const fieldsBySection = fieldResponses.reduce(
    (acc, field) => {
      const sectionId = field.tags['visual-inspection-section-response']
      if (!acc[sectionId]) {
        acc[sectionId] = []
      }
      acc[sectionId].push(field)
      return acc
    },
    {} as Record<string, GeckoVisualInspectionFieldResponse[]>
  )

  // Process each section
  sectionResponses.forEach((section) => {
    const sectionFields = fieldsBySection[section.id] || []

    // Sort fields by order
    sectionFields.sort((a, b) => a.data.order - b.data.order)

    sectionFields.forEach((field) => {
      findings.push({
        category: uppercase
          ? section.data.title.toUpperCase()
          : section.data.title,
        name: uppercase ? field.data.label.toUpperCase() : field.data.label,
        value: uppercase
          ? String(field.data.value || 'N/A').toUpperCase()
          : String(field.data.value || 'N/A'),
        findingSummary: field.data.notes.join(' ') || '',
      })
    })
  })

  return findings
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, sectionResponses, fieldResponses } = useVisualReportOps()
  const data = useMemo(
    () => formData.checklistFindingsData,
    [formData.checklistFindingsData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  // Process section and field responses into checklist findings
  const checklistFindings = useMemo(
    () => processChecklistFindings(sectionResponses, fieldResponses, true),
    [sectionResponses, fieldResponses]
  )

  return (
    <Page size='A4' style={PDFStyles.page} id='checklist-findings' wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'checklist-findings', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Checklist Findings' />

        {/* Findings Table */}
        <View style={{ marginBottom: 20 }}>
          {/* Table Header */}
          <View
            style={{
              flexDirection: 'row',
              backgroundColor: '#E5E7EB',
              borderBottom: '1 solid #9CA3AF',
              padding: 8,
            }}>
            <Text style={{ fontSize: 10, fontWeight: 700, width: '20%' }}>
              Category
            </Text>
            <Text style={{ fontSize: 10, fontWeight: 700, width: '20%' }}>
              Name
            </Text>
            <Text style={{ fontSize: 10, fontWeight: 700, width: '15%' }}>
              Value
            </Text>
            <Text style={{ fontSize: 10, fontWeight: 700, width: '45%' }}>
              Finding Summary
            </Text>
          </View>

          {/* Table Rows */}
          {checklistFindings.map((finding, index) => (
            <View
              key={index}
              style={{
                flexDirection: 'row',
                borderBottom: '1 solid #E5E7EB',
                padding: 8,
                backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB',
              }}>
              <Text style={{ fontSize: 9, width: '20%' }}>
                {finding.category}
              </Text>
              <Text style={{ fontSize: 9, width: '20%' }}>{finding.name}</Text>
              <Text style={{ fontSize: 9, width: '15%' }}>{finding.value}</Text>
              <Text style={{ fontSize: 9, width: '45%' }}>
                {finding.findingSummary}
              </Text>
            </View>
          ))}
        </View>

        {/* Scope Section */}
        {data.scope && (
          <View style={{ marginTop: 30 }}>
            <Text style={{ fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
              Scope:
            </Text>
            <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{data.scope}</Text>
          </View>
        )}
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<ChecklistFindingsData>()
  const {
    formData,
    handleChecklistFindingsFinish,
    sectionResponses,
    fieldResponses,
  } = useVisualReportOps()

  const onFinish = useCallback(
    async (values: ChecklistFindingsData) => {
      try {
        await handleChecklistFindingsFinish(values.scope || '')
        message.success('Checklist findings saved successfully!')
      } catch {
        message.error('Failed to save checklist findings')
      }
    },
    [handleChecklistFindingsFinish]
  )

  const initialValues = React.useMemo(
    () => formData.checklistFindingsData,
    [formData.checklistFindingsData]
  )

  // Process the checklist findings for display
  const checklistFindings = useMemo(
    () => processChecklistFindings(sectionResponses, fieldResponses, false),
    [sectionResponses, fieldResponses]
  )

  return (
    <StyledForm<ChecklistFindingsData>
      form={form}
      name='checklistFindingsForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      {/* Display the checklist findings table */}
      {checklistFindings.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3>Checklist Findings</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th
                  style={{
                    border: '1px solid #d9d9d9',
                    padding: 8,
                    textAlign: 'left',
                  }}>
                  Category
                </th>
                <th
                  style={{
                    border: '1px solid #d9d9d9',
                    padding: 8,
                    textAlign: 'left',
                  }}>
                  Name
                </th>
                <th
                  style={{
                    border: '1px solid #d9d9d9',
                    padding: 8,
                    textAlign: 'left',
                  }}>
                  Value
                </th>
                <th
                  style={{
                    border: '1px solid #d9d9d9',
                    padding: 8,
                    textAlign: 'left',
                  }}>
                  Finding Summary
                </th>
              </tr>
            </thead>
            <tbody>
              {checklistFindings.map((finding, index) => (
                <tr key={index}>
                  <td style={{ border: '1px solid #d9d9d9', padding: 8 }}>
                    {finding.category}
                  </td>
                  <td style={{ border: '1px solid #d9d9d9', padding: 8 }}>
                    {finding.name}
                  </td>
                  <td style={{ border: '1px solid #d9d9d9', padding: 8 }}>
                    {finding.value}
                  </td>
                  <td style={{ border: '1px solid #d9d9d9', padding: 8 }}>
                    {finding.findingSummary}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Form.Item
        name='scope'
        label='Scope'
        rules={[
          { required: true, message: 'Please enter the inspection scope' },
        ]}>
        <Input.TextArea
          rows={4}
          placeholder='Describe the scope of the inspection...'
        />
      </Form.Item>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Checklist Findings
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const ChecklistFindingsSection: ReportSection = {
  RenderReact,
  required: false,
  RenderPDF,
  id: 'checklistFindings',
  description: 'Inspection scope and checklist findings',
  title: 'Checklist Findings',
  requiredFields: requiredChecklistFindingsFields,
}

export const ChecklistFindingsSectionPDF = RenderPDF
